import {
  NavigateOptions,
  URLSearchParamsInit,
  createSearchParams,
  useNavigate,
} from 'react-router-dom';

type IsParameter<Part> = Part extends `:${infer ParamName}` ? ParamName : never;

type ParameterNames<Path> = Path extends `/${infer PartA}/${infer PartB}/`
  ? IsParameter<PartA> | ParameterNames<PartB>
  : IsParameter<Path>;

type UnknownInputParams = Record<string, string | number | undefined | null | (string | number)[]>;

type InputRouteParams<Path> = {
  [Key in ParameterNames<Path> as Key extends '*' ? '*' : Key]: Key extends '*'
    ? (string | number)[]
    : string | number;
} & UnknownInputParams;

type DynamicRouteObject<T> = T extends Routes
  ? {
      pathname: T;
      params: InputRouteParams<T>;
      search?: URLSearchParamsInit;
      hash?: string;
    }
  : never;

type DynamicRouteTemplateToString<Path> = Path extends `/${infer PartA}/${infer PartB}/`
  ? `/${PartA extends `:${string}` ? string : PartA}/${DynamicRouteTemplateToString<PartB>}/`
  : Path extends `/:${string}/`
  ? string
  : Path;

type Routes =
  | '/:tenant/'
  | '/:tenant/opportunities/:id/'
  | '/:tenant/opportunities/:id/analysis/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/attachments/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/change/interviews/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/change/attachments/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/change/details/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/custom-tests/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/interviews/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/history/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/questionnaires/'
  | '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/tests/'
  | '/:tenant/opportunities/:id/interview/:interview_id/'
  | '/:tenant/opportunities/:id/interview/:interview_id/opinion/'
  | '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/details/'
  | '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/attachments/'
  | '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/questionnaires/'
  | '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/history/'
  | '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/tests/'
  | '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/custom-tests/'
  | '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/interviews/'
  | '/:tenant/opportunities/:id/change/'
  | '/:tenant/opportunities/:id/change/allowedUsers/'
  | '/:tenant/opportunities/:id/change/general/'
  | '/:tenant/opportunities/:id/change/profile/'
  | '/:tenant/opportunities/:id/change/description/'
  | '/:tenant/opportunities/:id/change/publication/'
  | '/:tenant/opportunities/:id/change/stages/'
  | '/:tenant/opportunities/:id/details/'
  | '/:tenant/opportunities/:id/manager_dashboard/'
  | '/:tenant/opportunities/'
  | '/:tenant/settings/accessManagement/'
  | '/:tenant/settings/accessManagement/accessProfiles/:access_profile_id/'
  | '/:tenant/settings/accessManagement/accessProfiles/'
  | '/:tenant/settings/accessManagement/accessProfiles/create/'
  | '/:tenant/settings/accessManagement/users/'
  | '/:tenant/settings/candidateRegisterFields/'
  | '/:tenant/settings/candidateRegisterFields/candidate-source-options/'
  | '/:tenant/settings/company-info/'
  | '/:tenant/settings/emailTemplates/'
  | '/:tenant/settings/candidate-tags/'
  | '/:tenant/settings/integrations/'
  | '/:tenant/settings/integrations/external/'
  | '/:tenant/settings/integrations/publication/'
  | '/:tenant/settings/integrations/vixtingAdmission/'
  | '/:tenant/settings/requestJobPostingTemplates/'
  | '/:tenant/settings/interviewScriptTemplates/'
  | '/:tenant/settings/cs-staff/'
  | '/:tenant/settings/cs-staff/users/'
  | '/:tenant/settings/cs-staff/users/:user_id/'
  | '/:tenant/settings/cs-staff/users/create/'
  | '/:tenant/settings/cs-staff/staff-users/'
  | '/:tenant/settings/cs-staff/staff-users/:user_id/'
  | '/:tenant/settings/cs-staff/staff-users/create/'
  | '/:tenant/settings/cs-staff/import-files/'
  | '/:tenant/settings/cs-staff/tenant-config/'
  | '/:tenant/settings/cs-staff/mindmatch-integration/'
  | '/:tenant/settings/cs-staff/mindmatch-integration/:key_pair_id/'
  | '/:tenant/settings/cs-staff/external-integrations/'
  | '/:tenant/settings/cs-staff/external-integrations/api/'
  | '/:tenant/settings/cs-staff/external-integrations/api/create/'
  | '/:tenant/settings/cs-staff/external-integrations/sankhya/'
  | '/:tenant/settings/cs-staff/external-integrations/vixting/'
  | '/:tenant/talent-pool/'
  | '/:tenant/talent-pool/candidates/:applicant_id/attachments/'
  | '/:tenant/talent-pool/candidates/:applicant_id/change/attachments/'
  | '/:tenant/talent-pool/candidates/:applicant_id/change/details/'
  | '/:tenant/talent-pool/candidates/:applicant_id/custom-tests/'
  | '/:tenant/talent-pool/candidates/:applicant_id/details/'
  | '/:tenant/talent-pool/candidates/:applicant_id/history/'
  | '/:tenant/talent-pool/candidates/:applicant_id/questionnaires/'
  | '/:tenant/talent-pool/candidates/:applicant_id/tests/'
  | '/:tenant/talent-pool/candidates/:applicant_id/interviews/'
  | '/:tenant/request-job-postings/'
  | '/:tenant/request-job-postings/:id/'
  | '/:tenant/request-job-postings/:id/change/'
  | '/:tenant/request-job-postings/:id/details/'
  | '/:tenant/request-job-postings/:id/analysis/'
  | '/:tenant/request-job-postings/:id/history/';

type To = DynamicRouteTemplateToString<Routes> | DynamicRouteObject<Routes>;

interface TypedNavigateFunction {
  (to: To, options?: NavigateOptions): void;
}

export default function useTypedNavigate(): TypedNavigateFunction {
  const navigate = useNavigate();

  return (to: To, options?: NavigateOptions) => {
    if (typeof to === 'string') {
      navigate(to, options);
      return;
    }

    const params = Object.entries(to.params);
    const search = createSearchParams(to.search);
    let pathname = to.pathname as string;

    for (const [key, value] of params) {
      if (pathname.includes(`:${key}`)) {
        pathname = pathname.replace(`:${key}`, value as string);
      } else if (Array.isArray(value)) {
        search.delete(key);
        value.forEach((val) => search.append(key, val as string));
      } else {
        search.set(key, value as string);
      }
    }

    navigate({ pathname, search: search.toString(), hash: to.hash }, options);
  };
}
